import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { BADGE_EMOJIS } from "../lib/constants";
import RequestCard from "../components/RequestCard";
import Initials from "../components/Initials";
import { MapPin, Star, MessageCircle, Calendar } from "lucide-react";
import { useT, useI18n } from "../i18n/I18nContext";

function useFmtDate() {
  const { lang } = useI18n();
  const locale = { it: "it-IT", en: "en-GB", de: "de-DE" }[lang] || "it-IT";
  return (iso) => {
    try {
      return new Date(iso).toLocaleDateString(locale, { year: "numeric", month: "long", day: "numeric" });
    } catch { return iso; }
  };
}

export default function Profile() {
  const { userId } = useParams();
  const { user: me } = useAuth();
  const t = useT();
  const fmtDate = useFmtDate();
  const id = userId || me?.id;
  const [profile, setProfile] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [myRequests, setMyRequests] = useState([]);
  const [myHelping, setMyHelping] = useState([]);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    api.get(`/users/${id}`).then(({ data }) => { if (!cancelled) setProfile(data); });
    api.get(`/users/${id}/reviews`).then(({ data }) => { if (!cancelled) setReviews(data); });
    return () => { cancelled = true; };
  }, [id]);

  const isMe = me && (!userId || userId === me.id);

  useEffect(() => {
    if (!isMe) return;
    let cancelled = false;
    api.get("/requests", { params: { mine: "requested" } }).then(({ data }) => { if (!cancelled) setMyRequests(data); });
    api.get("/requests", { params: { mine: "helping" } }).then(({ data }) => { if (!cancelled) setMyHelping(data); });
    return () => { cancelled = true; };
  }, [isMe]);

  if (!profile) return <div className="max-w-3xl mx-auto px-6 py-16 text-mabw-soft" data-testid="profile-loading">{t("common.loading")}</div>;

  const badges = profile.badges || [];

  return (
    <div className="max-w-5xl mx-auto px-6 md:px-12 py-10 md:py-14 anim-fade-up" data-testid="profile-page">
      <div className="card-mabw p-6 md:p-10">
        <div className="flex flex-col md:flex-row gap-6 md:gap-10 items-start">
          <Initials name={`${profile.first_name} ${profile.last_initial}`} size={80} />
          <div className="flex-1">
            <div className="text-xs uppercase tracking-[0.22em] text-mabw-secondary font-semibold mb-2">
              {t("profile.overline")}
            </div>
            <h1 className="text-3xl md:text-4xl font-semibold leading-tight" style={{ fontFamily: "Outfit" }}>
              {profile.first_name} {profile.last_initial}
            </h1>
            <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-mabw-soft">
              <span className="inline-flex items-center gap-1.5"><MapPin className="w-4 h-4" /> {profile.city}</span>
              <span className="inline-flex items-center gap-1.5"><Calendar className="w-4 h-4" /> {t("profile.joined", { date: fmtDate(profile.created_at) })}</span>
              {profile.ratings_count > 0 && (
                <span className="inline-flex items-center gap-1.5">
                  <Star className="w-4 h-4 fill-current text-mabw-accent" />
                  {profile.avg_rating.toFixed(1)} ({profile.ratings_count} {t("common.reviewsShort")})
                </span>
              )}
            </div>

            <div className="mt-5 grid grid-cols-3 max-w-md gap-4">
              <div>
                <div className="text-3xl font-semibold text-mabw-primary" style={{ fontFamily: "Outfit" }}>{profile.completed_helps}</div>
                <div className="text-xs uppercase tracking-[0.18em] text-mabw-soft mt-0.5">{t("profile.helps_given")}</div>
              </div>
              <div>
                <div className="text-3xl font-semibold" style={{ fontFamily: "Outfit" }}>
                  {profile.ratings_count > 0 ? profile.avg_rating.toFixed(1) : "—"}
                </div>
                <div className="text-xs uppercase tracking-[0.18em] text-mabw-soft mt-0.5">{t("profile.avg_rating")}</div>
              </div>
              <div>
                <div className="text-3xl font-semibold" style={{ fontFamily: "Outfit" }}>{badges.length}</div>
                <div className="text-xs uppercase tracking-[0.18em] text-mabw-soft mt-0.5">{t("profile.badges")}</div>
              </div>
            </div>

            {!isMe && me && (
              <Link to={`/app/messaggi/${profile.id}`} className="btn-mabw mt-6" data-testid="profile-message-btn">
                <MessageCircle className="w-4 h-4" /> {t("profile.send_message")}
              </Link>
            )}
          </div>
        </div>

        {badges.length > 0 && (
          <div className="mt-8 pt-6 border-t border-mabw">
            <div className="text-xs uppercase tracking-[0.22em] text-mabw-secondary font-semibold mb-3">{t("profile.badges_title")}</div>
            <div className="flex flex-wrap gap-2" data-testid="profile-badges">
              {badges.map((bid) => (
                <span key={bid} title={t(`badges.${bid}.desc`)} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-stone-100 text-sm">
                  <span>{BADGE_EMOJIS[bid]}</span> {t(`badges.${bid}.name`)}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>

      <section className="mt-10">
        <h2 className="text-xl md:text-2xl font-semibold mb-4" style={{ fontFamily: "Outfit" }}>{t("profile.reviews_title")}</h2>
        {reviews.length === 0 ? (
          <div className="card-mabw p-8 text-center text-mabw-soft" data-testid="profile-no-reviews">
            {t("profile.no_reviews")}
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-4" data-testid="profile-reviews">
            {reviews.map((r) => (
              <div key={r.id} className="card-mabw p-5">
                <div className="flex items-center justify-between">
                  <div className="font-medium">{r.rater_name} {r.rater_last_initial}</div>
                  <div className="flex">
                    {[1,2,3,4,5].map((n) => (
                      <Star key={n} className={`w-4 h-4 ${n <= r.stars ? "fill-current text-mabw-accent" : "text-stone-300"}`} />
                    ))}
                  </div>
                </div>
                {r.comment && <p className="mt-2 text-sm text-mabw-text leading-relaxed">&ldquo;{r.comment}&rdquo;</p>}
                <div className="mt-3 text-xs text-mabw-soft">{fmtDate(r.created_at)}</div>
              </div>
            ))}
          </div>
        )}
      </section>

      {isMe && (
        <>
          <section className="mt-12">
            <h2 className="text-xl md:text-2xl font-semibold mb-4" style={{ fontFamily: "Outfit" }}>{t("profile.my_requests")}</h2>
            {myRequests.length === 0 ? (
              <div className="card-mabw p-8 text-center text-mabw-soft" data-testid="profile-no-requests">
                {t("profile.no_my_requests")}
              </div>
            ) : (
              <div className="grid md:grid-cols-2 gap-5" data-testid="profile-my-requests">
                {myRequests.map((r) => <RequestCard key={r.id} request={r} />)}
              </div>
            )}
          </section>

          <section className="mt-12">
            <h2 className="text-xl md:text-2xl font-semibold mb-4" style={{ fontFamily: "Outfit" }}>{t("profile.helping_in")}</h2>
            {myHelping.length === 0 ? (
              <div className="card-mabw p-8 text-center text-mabw-soft" data-testid="profile-no-helping">
                {t("profile.no_helping")}
              </div>
            ) : (
              <div className="grid md:grid-cols-2 gap-5" data-testid="profile-my-helping">
                {myHelping.map((r) => <RequestCard key={r.id} request={r} />)}
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
}
